import React from 'react';
import RNSelect, {
  CSSObjectWithLabel,
  ControlProps,
  GroupBase, InputProps, PlaceholderProps, Props, SingleValueProps,
  StylesConfig, ValueContainerProps, components, OptionProps,
} from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import { CustomButton } from '@shared/buttons';
import useTranslation from '@shared/hooks/useTranslation';
import { CustomDropdownCaretIcon, ClinicHoursIcon } from '@shared/icons';

import Checkbox from './Checkbox';

type SelectProps<Option, IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
  Async extends boolean = false> = {
    async?: Async;
    label?: string;
    required?: boolean;
    withFloatingLabel?: boolean;
    customIcon?: React.ReactNode;
    isTimePicker?: boolean;
    isModal?: boolean;
    withSupportOfCollapseTopics?: boolean,
  } & (Async extends false ? Props<Option, IsMulti, Group>
    : AsyncProps<Option, IsMulti, Group>);

const styles: StylesConfig = {
  menu: (base: CSSObjectWithLabel) => ({
    ...base,
    border: '1px solid #BABBC0 !important',
    height: 'auto',
    boxShadow: 'none !important',
  }),
  indicatorSeparator: (base: CSSObjectWithLabel) => ({
    ...base,
    alignSelf: 'center',
    height: '20px',
  }),
  singleValue: (base: CSSObjectWithLabel) => ({
    ...base,
    display: 'flex',
    alignItems: 'self-end',
  }),
  indicatorsContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    paddingRight: '6px !important',
  }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '0px 6px !important',
  }),
  // multiValue: (base: CSSObjectWithLabel) => ({
  //   ...base,
  //   marginTop: '20px',
  // }),
  multiValueLabel: (base: CSSObjectWithLabel) => ({
    ...base,
    backgroundColor: '#D7F1FF',
    padding: '4px 2px 4px 10px !important',
    color: '#0B409C !important',
    fontWeight: '600 !important',
    fontSize: '12px !important',
    borderTopLeftRadius: '100px',
    borderBottomLeftRadius: '100px',
  }),
  multiValueRemove: (base: CSSObjectWithLabel) => ({
    ...base,
    backgroundColor: '#D7F1FF',
    color: '#0B409C !important',
    ':hover': {
      backgroundColor: '#D7F1FF !important',
      color: 'red !important',
    },
    padding: '5px 8px 4px 8px !important',
    borderTopRightRadius: '100px',
    borderBottomRightRadius: '100px',
  }),
  noOptionsMessage: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '2px 0px 10px 0px !important',
  }),
  loadingMessage: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '2px 0px 10px 0px !important',
  }),
};

const floatingLabelStyles: StylesConfig = {
  menu: (base: CSSObjectWithLabel) => ({
    ...base,
    border: '1px solid #BABBC0 !important',
    height: 'auto',
    boxShadow: 'none !important',
  }),
  placeholder: (base: CSSObjectWithLabel, state: PlaceholderProps) => {
    const shouldFloatLabel = state.selectProps.menuIsOpen || state.hasValue;
    return shouldFloatLabel ? {
      display: 'none',
    } : base;
  },
  singleValue: (base: CSSObjectWithLabel, state: SingleValueProps) => {
    const shouldFloatLabel = state.selectProps.menuIsOpen || state.hasValue;
    return shouldFloatLabel ? {
      ...base,
      marginTop: '15px',
    } : base;
  },
  indicatorsContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    paddingRight: '6px !important',
  }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '0px 6px !important',
  }),
  input: (base: CSSObjectWithLabel, state: InputProps) => {
    const shouldFloatLabel = state.selectProps.menuIsOpen || state.hasValue;
    return shouldFloatLabel ? {
      ...base,
      marginTop: '15px',
    } : base;
  },
  control: (base: CSSObjectWithLabel, state: ControlProps) => {
    const shouldFloatLabel = state.selectProps.menuIsOpen || state.hasValue;
    return shouldFloatLabel ? {
      ...base,
      paddingTop: '0.12rem !important',
      paddingBottom: '0.12rem !important',
      paddingLeft: '0.35rem !important',
      paddingRight: '0.35rem !important',
      height: 'auto',
    } : {
      ...base,
      height: 'auto',
    };
  },
  indicatorSeparator: (base: CSSObjectWithLabel) => ({
    ...base,
    alignSelf: 'center',
    height: '20px',
  }),
  // multiValue: (base: CSSObjectWithLabel) => ({
  //   ...base,
  //   marginTop: '20px',
  // }),
  multiValueLabel: (base: CSSObjectWithLabel) => ({
    ...base,
    backgroundColor: '#D7F1FF',
    padding: '4px 2px 4px 10px !important',
    color: '#0B409C !important',
    fontWeight: '600 !important',
    fontSize: '12px !important',
    borderTopLeftRadius: '100px',
    borderBottomLeftRadius: '100px',
  }),
  multiValueRemove: (base: CSSObjectWithLabel) => ({
    ...base,
    backgroundColor: '#D7F1FF',
    color: '#0B409C !important',
    ':hover': {
      backgroundColor: '#D7F1FF !important',
      color: 'red !important',
    },
    padding: '5px 8px 4px 8px !important',
    borderTopRightRadius: '100px',
    borderBottomRightRadius: '100px',
  }),
  noOptionsMessage: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '2px 0px 10px 0px !important',
  }),
  loadingMessage: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: '2px 0px 10px 0px !important',
  }),
};

function NewValueContainer<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: ValueContainerProps<Option, IsMulti, Group> & {
  required?: boolean,
  withFloatingLabel?: boolean, customIcon?: React.ReactNode,
}) {
  const {
    selectProps, hasValue, children, required, withFloatingLabel,
  } = props;
  const shouldFloatLabel = selectProps.menuIsOpen || hasValue;
  const isMulti = selectProps?.isMulti;

  return (
    <components.ValueContainer {...props} className={isMulti && shouldFloatLabel ? 'custom-multi-select-spacer' : ''}>
      {shouldFloatLabel && withFloatingLabel && (
        <p
          className="custom-floating-select-label small text-muted"
        >
          {required && (
            <span className="text-danger">* </span>
          )}
          {(selectProps as any).label || selectProps.placeholder}
        </p>
      )}
      {children}
    </components.ValueContainer>
  );
}

function NewSingleValue<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: SingleValueProps<Option, IsMulti, Group> & { customIcon?: React.ReactNode, }) {
  const { children, customIcon } = props;

  return (
    <components.SingleValue {...props}>
      {customIcon && (
        <span style={{ marginRight: '8px' }}>{customIcon}</span>
      )}
      {children}
    </components.SingleValue>
  );
}

function NewPlaceholder<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: PlaceholderProps<Option, IsMulti, Group> & {
  required?: boolean, customIcon?: React.ReactNode,
}) {
  const { children, required, customIcon } = props;

  return (
    <components.Placeholder {...props}>
      <span>
        {required && (
          <span className="text-danger">* </span>
        )}
        {customIcon && (
          <span style={{ marginRight: '8px' }}>{customIcon}</span>
        )}
        {children}
      </span>
    </components.Placeholder>
  );
}

function CustomOptionWithSupportToHideSubTopics<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: OptionProps<Option, IsMulti, Group>) {
  const {
    isSelected, label, selectProps, data,
  } = props;
  const selectedValues = selectProps?.value as [];
  const isMulti = selectProps?.isMulti;
  const { isSubOption, parentId } = (data as any);

  const parentIsSelected = selectedValues?.find(
    (s: any) => s.key === parentId,
  );

  // display if not subOption or subOption but the parent is selected
  return !isSubOption || (isSubOption && parentIsSelected) ? (
    <components.Option {...props}>
      {isMulti && (
        <div style={{ paddingLeft: isSubOption ? '1rem' : '0' }}>
          <Checkbox
            type="checkbox"
            checked={isSelected}
            isReadOnly
            label={label}
          />
        </div>
      )}
      {!isMulti && (
        <span>{label}</span>
      )}
    </components.Option>
  ) : null;
}

function CustomOption<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: OptionProps<Option, IsMulti, Group>) {
  const {
    isSelected, label, selectProps, data,
  } = props;
  const isMulti = selectProps?.isMulti;
  const { isSubOption } = (data as any);
  return (
    <components.Option {...props}>
      {isMulti && (
        <div style={{ paddingLeft: isSubOption ? '1rem' : '0' }}>
          <Checkbox
            type="checkbox"
            checked={isSelected}
            isReadOnly
            label={label}
          />
        </div>
      )}
      {!isMulti && (
        <span>{label}</span>
      )}
    </components.Option>
  );
}

const CustomMenuList = (props: any) => {
  const {
    children, selectProps, isMulti, isLoading,
  } = props;
  const i18n = useTranslation('general');

  return (
    <div>
      <components.MenuList {...props}>
        <div>
          {children}
        </div>
        {(isMulti && !isLoading) && (
          <div className="custom-footer-button-container">
            <CustomButton
              id="select-footer-button"
              className="btn-lg w-100 select-footer-button"
              color="primary"
              onClick={selectProps.onMenuClose}
            >
              {i18n.button.done.name}
            </CustomButton>
          </div>
        )}
      </components.MenuList>
    </div>
  );
};

function Select<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
  Async extends boolean = false,
>({
  async, required, withFloatingLabel, isTimePicker, customIcon, isModal,
  withSupportOfCollapseTopics, ...rest
}: SelectProps<Option, IsMulti, Group, Async>) {
  const SelectComponent = async ? AsyncSelect : RNSelect;

  const baseStyles = withFloatingLabel ? floatingLabelStyles : styles;

  return (
    <SelectComponent
      {...rest}
      components={{
        ...rest.components,
        // eslint-disable-next-line react/no-unstable-nested-components
        ValueContainer: (props: any) => (
          <NewValueContainer
            required={required}
            withFloatingLabel={withFloatingLabel}
            customIcon={customIcon}
            {...props}
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        SingleValue: (props: any) => (
          <NewSingleValue
            customIcon={customIcon}
            {...props}
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        Placeholder: (props: any) => (
          <NewPlaceholder required={required} customIcon={customIcon} {...props} />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        DropdownIndicator: (props: any) => {
          if (isTimePicker) {
            return <ClinicHoursIcon {...props} />;
          }
          return <CustomDropdownCaretIcon {...props} />;
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        Option: withSupportOfCollapseTopics ? CustomOptionWithSupportToHideSubTopics : CustomOption,
        MenuList: CustomMenuList,
      }}
      styles={{
        ...baseStyles,
        ...rest.styles,
        indicatorSeparator: (base: CSSObjectWithLabel) => (isTimePicker ? {
          display: 'none',
        } : {
          ...base,
          alignSelf: 'center',
          height: '20px',
        }),
      }}
    />
  );
}

export default Select;
